import { useCallback, useEffect, useState } from "react";
import { ANALYTICS_EVENTS } from "../constants.js";
import { t } from "../utils/translate.js";
export function useSignUpStep1(api, config, onEmailValidated) {
    const [state, setState] = useState({
        email: config.initialEmail || '',
        errors: {},
        isLoading: false
    });
    useEffect(()=>{
        config.analyticsTrack?.(ANALYTICS_EVENTS.SIGNUP_IMPRESSION);
    }, []);
    const setEmail = useCallback((email)=>{
        setState((prev)=>({
                ...prev,
                email: email.trim(),
                errors: {}
            }));
    }, []);
    const submit = useCallback(async ()=>{
        if (!state.email) {
            setState((prev)=>({
                    ...prev,
                    errors: {
                        email: t(config, 'auth.error.requiredField')
                    }
                }));
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
            setState((prev)=>({
                    ...prev,
                    errors: {
                        email: t(config, 'auth.error.invalidEmail')
                    }
                }));
            return;
        }
        // Disposable-domain blocking is enforced authoritatively by the server
        // (`authRegister` returns `DISPOSABLE_EMAIL`), so step 1 does not run a
        // local check anymore. Step 2 surfaces that error inline if it fires.
        setState((prev)=>({
                ...prev,
                isLoading: true,
                errors: {}
            }));
        try {
            // Check SSO enforcement first
            const ssoResult = await api.getSsoEnforced(state.email);
            if (ssoResult.enforceSso && ssoResult.alias) {
                // SSO enforced — redirect to Keycloak with IDP hint
                const url = new URL(`${config.keycloakUrl}/realms/${config.keycloakRealm}/protocol/openid-connect/auth`);
                url.searchParams.set('client_id', config.keycloakClientId);
                url.searchParams.set('redirect_uri', window.location.origin);
                url.searchParams.set('response_type', 'code');
                url.searchParams.set('scope', 'openid');
                url.searchParams.set('kc_idp_hint', ssoResult.alias);
                window.location.href = url.toString();
                return;
            }
            // Deliberately no longer pre-checks whether the email is already registered
            // (PLA-259). Revealing that answer to an unauthenticated caller enables
            // email-address enumeration. The authRegister mutation returns a generic
            // error at submit time if the email is taken.
            setState((prev)=>({
                    ...prev,
                    isLoading: false
                }));
            config.analyticsTrack?.(ANALYTICS_EVENTS.SIGNUP_EMAIL_CONTINUE, {
                email: state.email
            });
            onEmailValidated(state.email);
        } catch  {
            setState((prev)=>({
                    ...prev,
                    isLoading: false,
                    errors: {
                        form: t(config, 'auth.error.networkError')
                    }
                }));
        }
    }, [
        state.email,
        api,
        config,
        onEmailValidated
    ]);
    return {
        email: state.email,
        errors: state.errors,
        isLoading: state.isLoading,
        setEmail,
        submit
    };
}

//# sourceMappingURL=useSignUpStep1.js.map